/**
 * KNK-1.12 / FR-22, FR-19 — Settings → Knock Mode → Dispositions and Quick buttons.
 *
 * Rendered by KnockModeSettings only while Knock Mode is on and the viewer holds
 * field.settings.manage (FR-11: when off, no knocking setting appears anywhere).
 *
 * ⚠️ Nothing here is the gate. RLS and the guards in 20261005a8 refuse everything this screen
 * would refuse; when they do, the toast carries the database's own sentence.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, ArchiveRestore, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import { useTenant } from '@/contexts/TenantContext';
import {
  DISPOSITIONS_COPY as COPY,
  QUICK_BUTTONS_COPY,
  quickSlotsByCode,
  ruleSummary,
  type Disposition,
  type DispositionPatch,
  type QuickButtonRow,
} from '@/lib/knockDispositions';
import {
  createDisposition,
  deleteDisposition,
  fetchDispositions,
  fetchQuickButtons,
  fetchTeams,
  setQuickButtons,
  updateDisposition,
  type TeamOption,
} from '@/services/knockDispositionService';

import { DispositionDialog } from './DispositionDialog';
import { QuickButtonsCard } from './QuickButtonsCard';

export function KnockDispositionSettings({ onQuickButtonsChanged }: { onQuickButtonsChanged?: () => void }) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id ?? null;
  const { toast } = useToast();

  const [dispositions, setDispositions] = useState<Disposition[]>([]);
  const [rows, setRows] = useState<QuickButtonRow[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Disposition | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Disposition | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setError(null);
    try {
      const [d, q, t] = await Promise.all([fetchDispositions(), fetchQuickButtons(), fetchTeams()]);
      setDispositions(d);
      setRows(q);
      setTeams(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const fail = (e: unknown) =>
    toast({ title: 'Not changed', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });

  const slotOf = useMemo(() => quickSlotsByCode(rows), [rows]);
  const archivedCount = dispositions.filter((d) => !d.active).length;
  const shown = dispositions.filter((d) => showArchived || d.active);

  const save = async (patch: DispositionPatch, newCode: string | null) => {
    try {
      if (editing === 'new') {
        if (!tenantId || !newCode) throw new Error('No active account.');
        await createDisposition(tenantId, newCode, patch);
      } else if (editing) {
        await updateDisposition(editing.id, patch);
      }
      await load();
      toast({ title: 'Disposition saved' });
    } catch (e) {
      fail(e);
      throw e;
    }
  };

  const act = async (d: Disposition, fn: () => Promise<void>, done: string) => {
    setBusyId(d.id);
    try {
      await fn();
      await load();
      toast({ title: done });
    } catch (e) {
      fail(e);
    } finally {
      setBusyId(null);
    }
  };

  const saveButtons = async (teamId: number | null, codes: string[]) => {
    try {
      await setQuickButtons(teamId, codes);
      await load();
      if (teamId === null) onQuickButtonsChanged?.();
      toast({ title: QUICK_BUTTONS_COPY.saved });
    } catch (e) {
      fail(e);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Dispositions…
      </div>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>Could not read Dispositions: {error}</AlertDescription>
        </Alert>
      )}

      <QuickButtonsCard dispositions={dispositions} rows={rows} teams={teams} onSave={saveButtons} />

      <Card data-testid="knock-dispositions">
        <CardHeader className="gap-3 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
          <div className="space-y-1.5">
            <CardTitle>{COPY.title}</CardTitle>
            <CardDescription>{COPY.description}</CardDescription>
          </div>
          <Button size="sm" onClick={() => setEditing('new')} className="shrink-0 self-start">
            <Plus className="mr-1 h-4 w-4" /> {COPY.add}
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {archivedCount > 0 && (
            <div className="flex items-center gap-2">
              <Switch id="disp-show-archived" checked={showArchived} onCheckedChange={setShowArchived} />
              <Label htmlFor="disp-show-archived">{COPY.showArchived} ({archivedCount})</Label>
            </div>
          )}
          <ul className="divide-y divide-border rounded-md border border-border">
            {shown.map((d) => {
              const rules = ruleSummary(d);
              const slot = slotOf.get(d.code);
              return (
                <li key={d.id} data-testid={`disposition-${d.code}`}
                  className={`flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between ${d.active ? '' : 'opacity-60'}`}>
                  <div className="flex min-w-0 gap-3">
                    <span className="mt-1 h-4 w-4 shrink-0 rounded-full border border-border" style={{ backgroundColor: d.color }} aria-hidden />
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-medium text-foreground">{d.name}</span>
                        <code className="text-xs text-muted-foreground">{d.code}</code>
                        <Badge variant="outline">{d.category}</Badge>
                        {slot !== undefined && <Badge variant="secondary">Quick {slot}</Badge>}
                        {!d.active && <Badge variant="secondary">{COPY.archived}</Badge>}
                      </div>
                      {rules.length > 0 && <p className="text-xs text-muted-foreground">{rules.join(' · ')}</p>}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-1">
                    {busyId === d.id && <Loader2 className="m-2 h-4 w-4 animate-spin" />}
                    <Button size="sm" variant="ghost" onClick={() => setEditing(d)} disabled={busyId !== null}>
                      <Pencil className="mr-1 h-4 w-4" /> {COPY.edit}
                    </Button>
                    {d.active ? (
                      <Button size="sm" variant="ghost" disabled={busyId !== null}
                        onClick={() => act(d, () => updateDisposition(d.id, { is_active: false }), `${d.name} archived`)}>
                        <Archive className="mr-1 h-4 w-4" /> {COPY.archive}
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" disabled={busyId !== null}
                        onClick={() => act(d, () => updateDisposition(d.id, { is_active: true }), `${d.name} restored`)}>
                        <ArchiveRestore className="mr-1 h-4 w-4" /> {COPY.restore}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
                      disabled={busyId !== null} onClick={() => setDeleting(d)} aria-label={`${COPY.delete} ${d.name}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <DispositionDialog
        open={editing !== null}
        disposition={editing === 'new' || editing === null ? undefined : editing}
        existingCodes={dispositions.map((d) => d.code)}
        onOpenChange={(o) => !o && setEditing(null)}
        onSave={save}
      />

      <AlertDialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{COPY.delete} {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>{COPY.deleteConfirm}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const d = deleting;
                setDeleting(null);
                if (d) void act(d, () => deleteDisposition(d.id), `${d.name} deleted`);
              }}
            >
              {COPY.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
